import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  ElementRef,
  Inject,
  NgZone,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation
} from "@angular/core";
import { LOCAL_STORAGE, WINDOW } from "@ng-web-apis/common";
import {
  combineLatest,
  delay,
  filter,
  from,
  fromEvent,
  map,
  merge,
  Observable,
  ReplaySubject,
  startWith,
  take,
  tap
} from "rxjs";
import { Router, RouterModule, RouterOutlet } from "@angular/router";
import { APP_READY, BREAKPOINT, switchLanguage } from "@icilalune/shell-state";
import { TranslocoModule, TranslocoService } from "@ngneat/transloco";
import { Store } from "@ngrx/store";
import { LanguageType } from "@icilalune/types";
import { TunnelStage } from "@icilalune/three-tunnel";
import { DOCUMENT, NgIf } from "@angular/common";
import { ROUTE_IS_HOME, ROUTE_IS_SETTINGS } from "@icilalune/router";
import { ScrollDispatcher } from "@angular/cdk/overlay";
import {
  AxeptioService,
  GeoLocService,
  PortalService,
  RouterStaticLinkDirectiveModule,
  setThemeColorVars
} from "@icilalune/utils";
import { ComponentStore } from "@ngrx/component-store";
import {
  COLOR_THEME,
  CURRENT_MAIN_MENU_ITEM,
  CURRENT_MAIN_MENU_TREE_ITEM,
  LUNE_MAIN_MENU_ITEM_ACTIVE,
  MAIN_MENU,
  MAIN_MENU_BREADCRUMBS,
  ROUTED_ENTITY_ALTERNATIVES
} from "@nx-icilalune/data/icilalune/tokens";
import { TranslationItem, Tree } from "@nx-icilalune/drupal-types";
import {
  IciLaLuneRouteComponent,
  MainMenuItem,
  MainMenuTreeItem,
  MenuItemContext
} from "@nx-icilalune/data/icilalune/types";
import {
  COMPONENT_BREADCRUMBS,
  COMPONENT_MAIN_MENU_SELECTED_TREE_ITEM,
  COMPONENT_MAIN_MENU_TREE,
  MainMenuComponentModule
} from "../main-menu/main-menu.component";
import { isScullyGenerated, isScullyRunning, TransferStateService } from "@scullyio/ng-lib";
import { Breakpoint } from "@icilalune/utils/responsive";
import { MUTE_VIMEO_VIDEO } from "@nx-icilalune/icilalune/media/tokens";
import { AudioService } from "@icilalune/utils/audio";
import { IcilaluneLanguageModule, LanguageService } from "@madeinlune/dialog/language-switcher";
import { VerticalInfosComponentModule } from "../vertical-infos/vertical-infos.component";
import { ToolBarComponentModule } from "../tool-bar/tool-bar.component";
import { IcilaluneLogoComponentModule } from "../icilalune-logo/icilalune-logo.component";
import { ObserversModule } from "@angular/cdk/observers";
import { MainHeaderComponentModule } from "../main-header/main-header.component";
import { SvgIconComponentModule } from "@nx-icilalune/icilalune/media";
import { LoadingDirective } from "@nx-icilalune/icilalune/loading";
import { SettingsLinkComponent } from "../settings-link/settings-link.component";
import { LetModule, PushModule } from "@ngrx/component";
import { PortalModule } from "@angular/cdk/portal";

interface MainLayoutStore {

  translationItems?: TranslationItem[];
  ready: boolean;

}

@Component({
  selector: "sh-ui-main-layout",
  standalone: true,
  template: `
    <ng-container #stageContainer></ng-container>

    <header>
      <sh-ui-main-header [mainMenu]="mainMenu$ | ngrxPush"
                         [logoSplitMode]="logoSplitMode$|ngrxPush"></sh-ui-main-header>
    </header>

    <main #mainContainer [class.is-home]="routeIsHome$ | ngrxPush" class="b-lune-main-group">

      <ng-container *transloco="let t">
        <ng-container *ngIf="t('loading.generic') as message">
          <ng-container
            *i2lLoading="
            !(ready$ | ngrxPush);
            loadingType: 'light';
            message: message
          "
          ></ng-container>
        </ng-container>
      </ng-container>

      <router-outlet #outlet="outlet" (activate)="onActivate($event)"
                     (deactivate)="onDeActivate($event)"></router-outlet>
    </main>
    <footer>
      <sh-ui-vertical-infos class="b-fixed-layer full"
                            [class.stand-by]="!(luneMenuItemActive$|ngrxPush)"></sh-ui-vertical-infos>
      <sh-ui-tool-bar
        #toolBarComponent
        [class.stand-by]="!(luneMenuItemActive$|ngrxPush)"
        class="b-fixed-layer"
        (playMusic)="playAudio($event)"
        [isLune]="(luneMenuItemActive$|ngrxPush) || false"
      ></sh-ui-tool-bar>
      <ul class="main-functions b-infos-link b-fixed-layer" [class.stand-by]="!(luneMenuItemActive$|ngrxPush)">
        <li class="small-icon">
          <button [class.no-translation]="!(hasTranslations$|ngrxPush)" class="btn-language"
                  (click)="onSwitchLanguage()">
            <i2l-svg-icon icon="globe"></i2l-svg-icon>
          </button>
        </li>
        <li class="small-icon">
          <sh-ui-settings-link></sh-ui-settings-link>
        </li>
      </ul>
    </footer>

    <audio #audioElement src="./assets/music/icilalune-music.mp3" loop="true"></audio>

    <ng-container
      *ngrxLet="fixedLayerContentPortal$; let fixedLayerContentPortal"
    >
      <ng-container *ngIf="fixedLayerContentPortal">
        <ng-template
          [cdkPortalOutlet]="fixedLayerContentPortal"
        ></ng-template>
      </ng-container>
    </ng-container>

    <!--<div class="b-before-3d-ready">
    </div>-->
  `,
  styleUrls: ["./main-layout.component.scss"],
  encapsulation: ViewEncapsulation.None,
  imports: [
    RouterModule,
    VerticalInfosComponentModule,
    ToolBarComponentModule,
    MainMenuComponentModule,
    IcilaluneLogoComponentModule,
    ObserversModule,
    RouterStaticLinkDirectiveModule,
    MainHeaderComponentModule,
    SvgIconComponentModule,
    LoadingDirective,
    SettingsLinkComponent,
    PushModule,
    IcilaluneLanguageModule,
    NgIf,
    TranslocoModule,
    PortalModule,
    LetModule
  ],
  providers: [
    {
      provide: COMPONENT_MAIN_MENU_TREE,
      deps: [MAIN_MENU],
      useFactory: (mainMenu$: Observable<Tree<MainMenuTreeItem>>) => {
        return mainMenu$.pipe(filter((mainMenu) => !!mainMenu));
      }
    },
    {
      provide: COMPONENT_MAIN_MENU_SELECTED_TREE_ITEM,
      deps: [CURRENT_MAIN_MENU_ITEM],
      useFactory: (currentMainMenuItem$: Observable<MainMenuTreeItem>) => {
        return currentMainMenuItem$;
      }
    },
    {
      provide: COMPONENT_BREADCRUMBS,
      deps: [MAIN_MENU_BREADCRUMBS],
      useFactory: (mainMenuBreadcrumbs$: Observable<MainMenuItem[]>) => {
        return mainMenuBreadcrumbs$;
      }
    },
    {
      provide: MUTE_VIMEO_VIDEO,
      deps: [AudioService],
      useFactory: (audioService: AudioService) => {
        return audioService.mute$;
      }
    }
  ]
})
export class MainLayoutComponent
  extends ComponentStore<MainLayoutStore>
  implements AfterViewInit {

  readonly translationItems$ = this.select(s => s.translationItems);

  readonly hasTranslations$: Observable<boolean> = this.translationItems$.pipe(
    map(translationsItems => {
      return (translationsItems && translationsItems?.length > 0) || false;
    })
  );

  observerDisconnected = false;

  fixedLayerContentPortal$ = this.portalService.fixedLayerContentPortal$;

  @ViewChild("stageContainer", { read: ViewContainerRef })
  stageContainer!: ViewContainerRef;

  @ViewChild("mainContainer", { read: ElementRef<HTMLElement> })
  mainContainer!: ElementRef<HTMLElement>;

  @ViewChild("toolBarComponent", { read: ElementRef<HTMLElement> })
  toolBarComponent!: ElementRef<HTMLElement>;

  @ViewChild("audioElement")
  audioElement!: ElementRef<HTMLAudioElement>;

  readonly ready$ = this.select((s) => s.ready);

  logoSplitMode$: Observable<boolean> = this.luneMenuItemActive$.pipe(
    map((luneMenuItemActive) => {
      if((isScullyRunning() || isScullyGenerated())){
        return true;
      }
      return !luneMenuItemActive;
    })
  );
  stageTunnelInstance$: ReplaySubject<TunnelStage> =
    new ReplaySubject<TunnelStage>(1);
  hasScroll$ = merge(
    fromEvent(this.window, "wheel"),
    fromEvent(this.window, "scroll")
  ).pipe(
    map(() => this.window.scrollY > 0),
    startWith(false)
  );
  readonly getAppReady = this.effect((combined$: Observable<[boolean]>) => {
    return combined$.pipe(
      filter(([appReady]) => {
        return !!appReady;
      }),
      take(1),
      delay(0),
      tap((appReady) => {
        const loadingElement = this.#getLoadingElement();
        if (loadingElement) {
          loadingElement.addEventListener("animationend", (event) => {
              if ((event as AnimationEvent).animationName === "destroy") {
                this.#removeLoadingElement();
              }
            }
          );
          loadingElement.classList.add("app-ready");
        }
      })
    );
  });

  readonly getHasScrolled = this.effect(
    (combined$: Observable<[boolean, boolean]>) => {
      return combined$.pipe(
        tap(([hasScrolled, currentMenuItemIsFirst]) => {
          this.el.nativeElement.classList.remove("has-scroll");
          if (hasScrolled) {
            this.el.nativeElement.classList.add("has-scroll");
          }
        })
      );
    }
  );
  readonly getIsHome = this.effect(
    (combined$: Observable<[boolean, TunnelStage]>) => {
      return combined$.pipe(
        tap(([isHome, stageTunnel]) => {
          if (stageTunnel) {
            if (isHome) {
              stageTunnel.play();
            } else {
              stageTunnel.pause();
            }
          }
        })
      );
    }
  );
  #stageTunnelRef!: ComponentRef<TunnelStage>;
  #loadStage3D = true;
  #stage3DLoading = false;
  #stage3DLoaded = false;
  #sceneReady$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  setColorTheme = this.effect(
    (colorTheme$: Observable<MenuItemContext | undefined>) => {
      return colorTheme$.pipe(
        map((colorTheme) => {
          if (this.mainContainer && this.toolBarComponent) {
            setThemeColorVars(colorTheme, this.mainContainer.nativeElement);
            setThemeColorVars(colorTheme, this.toolBarComponent.nativeElement);
          }
        })
      );
    }
  );

  constructor(
    @Inject(LOCAL_STORAGE) private localStorage: Storage,
    @Inject(BREAKPOINT)
    public breakpoint$: Observable<Breakpoint>,
    @Inject(WINDOW) private window: Window,
    @Inject(DOCUMENT) private document: Document,
    @Inject(ROUTE_IS_SETTINGS) private routeIsSettings$: Observable<boolean>,
    @Inject(ROUTE_IS_HOME) protected routeIsHome$: Observable<boolean>,
    @Inject(APP_READY) private appReady$: Observable<boolean>,
    @Inject(MAIN_MENU) protected mainMenu$: Observable<Tree<MainMenuTreeItem>>,
    @Inject(CURRENT_MAIN_MENU_ITEM)
    protected currentMainMenuItem$: Observable<MainMenuItem>,
    @Inject(MAIN_MENU_BREADCRUMBS)
    protected mainMenuBreadcrumbs$: Observable<MainMenuItem[]>,
    @Inject(CURRENT_MAIN_MENU_TREE_ITEM)
    protected currentMainMenuTreeItem$: Observable<MainMenuTreeItem>,
    @Inject(COLOR_THEME)
    protected colorTheme$: Observable<MenuItemContext>,
    @Inject(LUNE_MAIN_MENU_ITEM_ACTIVE)
    protected luneMenuItemActive$: Observable<boolean>,
    private translocoService: TranslocoService,
    private store: Store<any>,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private scrollDispatcher: ScrollDispatcher,
    private el: ElementRef<HTMLElement>,
    private axeptioService: AxeptioService,
    private geoLocService: GeoLocService,
    private appRef: ApplicationRef,
    @Inject(ROUTED_ENTITY_ALTERNATIVES) private contentAlternatives$: Observable<TranslationItem[]>,
    private router: Router,
    transferStateService: TransferStateService,
    private languageService: LanguageService,
    private portalService: PortalService
  ) {
    super({ ready: false });

    if (isScullyRunning()) {
      this.store.subscribe((store) => {
        if (store.drupalDatas) {
          transferStateService.setState("drupalDatas", store.drupalDatas);
        }
      });
    }

    this.patchState(this.contentAlternatives$.pipe(map(contentAlternatives => {
      return { translationItems: contentAlternatives };
    })));

    this.getAppReady(combineLatest([this.appReady$]));
    this.getIsHome(
      combineLatest([this.routeIsHome$, this.stageTunnelInstance$])
    );
    /* const hasScrolled$ = combineLatest([
      this.hasScroll$.pipe(distinctUntilChanged()),
      this.currentMenuItemIsFirst$.pipe(distinctUntilChanged()),
    ]);
    this.getHasScrolled(hasScrolled$);*/

    if (this.#loadStage3D && !isScullyRunning()) {
      this.ngZone.runOutsideAngular(() => {
        if (this.document.defaultView) {
          if ("requestIdleCallback" in this.document.defaultView) {
            this.document.defaultView.requestIdleCallback(() => {
              this.create3D();
            });
          } else {
            this.create3D();
          }
        }

      });
    }

    if (!isScullyRunning()) {
      this.axeptioService.init();
    } else {
      this.#removeLoadingElement();
    }
  }

  create3D() {
    this.loadStage3D()
      .pipe(
        take(1)
      )
      .subscribe();
  }

  loadStage3D(): Observable<any> {
    this.#stage3DLoading = true;
    return from(
      import("@icilalune/three-tunnel").then((m) => {
        if (this.stageContainer) {
          this.#stage3DLoading = false;
          this.#stage3DLoaded = true;
          this.stageContainer.clear();
          this.#stageTunnelRef = this.stageContainer.createComponent(
            m.StageComponent
          );
          this.stageTunnelInstance$.next(this.#stageTunnelRef.instance);
          this.#stageTunnelRef.instance.sceneReady
            .pipe(take(1))
            .subscribe(() => {
              this.#sceneReady$.next(true);
            });
        }
      })
    );
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet?.activatedRouteData?.["animation"];
  }

  changeRenderMode(renderMode: string) {
    this.localStorage.setItem("renderMode", renderMode);
    const url = location.href;
    location.href = url;
  }

  autoPlay() {
  }

  ngAfterViewInit(): void {
    this.setColorTheme(this.colorTheme$);
    const audioNativeElement = this.audioElement?.nativeElement;
    if (audioNativeElement) {
      audioNativeElement.addEventListener("loadeddata", (event) => {
        if (audioNativeElement.readyState > 0) {
        }
      });
    }
  }

  selectLanguage(language: string, path: string) {
    this.store.dispatch(
      switchLanguage({ language: language as LanguageType, path })
    );
  }

  #getLoadingElement(): Element | null {
    return this.document.getElementsByClassName("b-initial-loading").item(0);
  }

  #removeLoadingElement() {
    const loadingElement = this.#getLoadingElement();
    if (loadingElement) {
      loadingElement.remove();
    }
  }

  playAudio(play: boolean) {
    const audioNativeElement = this.audioElement?.nativeElement;
    if (audioNativeElement) {
      this.reallyPlayAudio(play);
    }
  }

  reallyPlayAudio(play: boolean) {
    const audioNativeElement = this.audioElement.nativeElement;
    if (audioNativeElement) {
      if (play) {
        audioNativeElement.play();
      } else {
        audioNativeElement.pause();
      }
    }
  }

  onSwitchLanguage() {
    this.languageService.switch();
  }

  onActivate(component: IciLaLuneRouteComponent) {
    if (component.ready$) {
      this.patchState(
        component.ready$
          .pipe(
            delay(100),
            filter((ready) => !!ready),
            take(1)
          )
          .pipe(
            map((ready) => {
              return { ready };
            })
          )
      );
    } else {
      this.patchState({ ready: true });
    }
  }

  onDeActivate(component: IciLaLuneRouteComponent) {
    this.patchState({ ready: false });
  }

}
